import React from 'react';
import { useParams } from 'react-router-dom';
import { Spinner } from '../../../components/UI';
import { isMktError, marketingPublic, MktPreferencePage } from '../../../services/marketing/marketing';
import { PublicMessage, PublicShell } from './components/PublicShell.component';

type State = { kind: 'loading' } | { kind: 'done'; page: MktPreferencePage } | { kind: 'failed'; message: string };

// #region EmailConfirm
/**
 * /email/confirm/:token — the double opt-in link. Confirms on arrival (the token
 * is the only credential and confirming twice is harmless), then says what they
 * signed up for.
 */
const EmailConfirm: React.FunctionComponent<{}> = (): React.ReactElement => {
    const { token = '' } = useParams<{ token: string }>();
    const [state, setState] = React.useState<State>({ kind: 'loading' });

    React.useEffect(() => {
        document.title = 'Subscription confirmed';
        let live = true;
        (async () => {
            const r = await marketingPublic.confirm(token);
            if (!live) return;
            setState(isMktError(r) ? { kind: 'failed', message: r.message } : { kind: 'done', page: r });
        })();
        return () => { live = false; };
    }, [token]);

    if (state.kind === 'loading') return <PublicShell><div className="flex justify-center py-16"><Spinner show={true} /></div></PublicShell>;
    if (state.kind === 'failed') {
        return <PublicShell><PublicMessage title="We couldn't confirm that" body={`${state.message} If you signed up recently, use the link in the newest confirmation email.`} /></PublicShell>;
    }

    const { page } = state;
    const subscribed = page.topics.filter((t) => t.subscribed);
    return (
        <PublicShell workspace={page.workspace}>
            <PublicMessage tone="ok" title="You're subscribed" body={<>Thanks for confirming <span className="font-medium text-zinc-800 dark:text-zinc-200">{page.email_masked}</span>.</>}>
                {subscribed.length ? (
                    <ul className="mx-auto mt-6 max-w-sm space-y-2 text-left">
                        {subscribed.map((t) => (
                            <li key={t.key} className="rounded-xl border border-surface-border bg-surface px-4 py-3">
                                <span className="block text-sm font-medium">{t.name}</span>
                                {t.description ? <span className="mt-0.5 block text-sm text-zinc-600 dark:text-zinc-400">{t.description}</span> : null}
                            </li>
                        ))}
                    </ul>
                ) : null}
                <p className="mx-auto mt-6 max-w-sm text-xs text-zinc-500 dark:text-zinc-400">Every email has a link at the bottom to change what you get or unsubscribe.</p>
            </PublicMessage>
        </PublicShell>
    );
};

export default EmailConfirm;
